import React, { useState } from "react";
import { useAuthForm } from "../../context/AuthFormContext";
import { RegisterPayload } from "../../services/authService";
import PhoneVerificationInline from "../auth/PhoneVerificationInline";
import Input from "../ui/Input";
import Button from "../ui/Button";
import FormError from "../ui/FormError";
import FormGroup from "../ui/FormGroup";
import {
  validateEmail,
  validatePassword,
  validateName,
} from "../../utils/validation";
import {
  applyPhoneMask,
  isValidMaskedPhone,
  normalizePhoneForSubmit,
} from "../../utils/phoneMask";
import checkCircle from "../../img/check-circle.svg";

interface RegisterViewProps {
  identifier: string;
  identifierType: "email" | "phone" | "unknown";
}

interface RegisterFormErrors {
  name?: string;
  phone?: string;
  email?: string;
  password?: string;
  politics?: string;
  general?: string;
}

const RegisterView: React.FC<RegisterViewProps> = ({
  identifier,
  identifierType,
}) => {
  const { state, register } = useAuthForm();
  const [isPoliticsAccepted, setIsPoliticsAccepted] = useState(false);

  const [email, setEmail] = useState(
    identifierType === "email" ? identifier : "",
  );
  const [phone, setPhone] = useState(
    identifierType === "phone" ? identifier : "",
  );
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLegalEntity, setIsLegalEntity] = useState(false);
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const [errors, setErrors] = useState<RegisterFormErrors>({});

  const clearFieldError = (field: keyof RegisterFormErrors) => {
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
      general: undefined,
    }));
  };

  console.log(setIsLegalEntity);

  const handlePhoneChange = (value: string) => {
    const maskedValue = applyPhoneMask(value);

    setPhone((prevPhone) => {
      if (prevPhone !== maskedValue && phoneVerified) {
        setPhoneVerified(false);
      }
      return maskedValue;
    });

    clearFieldError("phone");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nextErrors: RegisterFormErrors = {};

    const trimmedName = name.trim();
    const trimmedPassword = password.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPhone = phone.trim();

    if (!trimmedName) {
      nextErrors.name = "Введите имя";
    } else {
      const nameValidation = validateName(trimmedName);
      if (!nameValidation.valid) {
        nextErrors.name = nameValidation.error!;
      }
    }

    if (!trimmedPassword) {
      nextErrors.password = "Введите пароль";
    } else {
      const passwordValidation = validatePassword(trimmedPassword);
      if (!passwordValidation.valid) {
        nextErrors.password = passwordValidation.error!;
      }
    }

    if (identifierType === "email") {
      if (!trimmedPhone) {
        nextErrors.phone = "Введите телефон";
      } else if (!isValidMaskedPhone(trimmedPhone)) {
        nextErrors.phone = "Введите телефон полностью";
      } else if (!phoneVerified) {
        nextErrors.phone = "Телефон должен быть подтверждён";
      }
    }

    if (identifierType === "phone") {
      if (!trimmedEmail) {
        nextErrors.email = "Введите email";
      } else {
        const emailValidation = validateEmail(trimmedEmail);
        if (!emailValidation.valid) {
          nextErrors.email = emailValidation.error!;
        }
      }
    }

    if (!isPoliticsAccepted) {
      nextErrors.politics =
        "Необходимо согласиться с обработкой персональных данных и публичной офертой";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});

    const payload: RegisterPayload = {
      email:
        identifierType === "email"
          ? identifier.trim().toLowerCase()
          : trimmedEmail,
      password: trimmedPassword,
      name: trimmedName,
      phone:
        identifierType === "phone"
          ? normalizePhoneForSubmit(identifier)
          : normalizePhoneForSubmit(trimmedPhone),
      isLegalEntity,
      subscribeToNewsletter,
    };

    try {
      await register(payload);
    } catch {
      setErrors({
        general: "Не удалось завершить регистрацию. Попробуйте ещё раз.",
      });
    }
  };

  const hasValue = (value: string) => value.trim().length > 0;

  const showNameLabel = hasValue(name);
  const showPhoneLabel =
    identifierType === "phone" ? hasValue(identifier) : hasValue(phone);
  const showEmailLabel =
    identifierType === "email" ? hasValue(identifier) : hasValue(email);
  const showPasswordLabel = hasValue(password);

  const hasNameError = Boolean(errors.name);
  const hasEmailError = Boolean(errors.email);
  const hasPasswordError = Boolean(errors.password);
  const hasPoliticsError = Boolean(errors.politics);

  return (
    <div className="auth-view">
      <form onSubmit={handleSubmit} noValidate>
        <FormGroup>
          <div className={`auth-field ${hasNameError ? "is-error" : ""}`}>
            {showNameLabel && (
              <label htmlFor="register-name" className="auth-field__label">
                ФИО
              </label>
            )}
            <Input
              id="register-name"
              type="text"
              value={name}
              error={hasNameError}
              onChange={(e) => {
                setName(e.target.value);
                clearFieldError("name");
              }}
              placeholder="ФИО"
              disabled={state.status === "submitting"}
              clearable
              onClear={() => {
                setName("");
                clearFieldError("name");
              }}
            />
          </div>
          <FormError message={errors.name} />
        </FormGroup>

        {identifierType === "email" && (
          <PhoneVerificationInline
            phone={phone}
            setPhone={handlePhoneChange}
            phoneVerified={phoneVerified}
            setPhoneVerified={(value) => {
              setPhoneVerified(value);
              if (value) {
                clearFieldError("phone");
              }
            }}
            error={errors.phone}
            disabled={state.status === "submitting"}
          />
        )}

        {identifierType === "phone" && (
          <FormGroup>
            <div className="auth-field">
              {showPhoneLabel && (
                <label htmlFor="register-phone" className="auth-field__label">
                  Телефон
                </label>
              )}
              <Input
                id="register-phone"
                type="tel"
                value={identifier}
                readOnly
                placeholder="Телефон"
                disabled={state.status === "submitting"}
                className="auth-input-disabled"
              />
            </div>

            <div className="auth-check">
              <img src={checkCircle} alt="" className="auth-check__icon" />
              <span>Подтверждён</span>
            </div>
          </FormGroup>
        )}

        {identifierType === "phone" && (
          <FormGroup>
            <div className={`auth-field ${hasEmailError ? "is-error" : ""}`}>
              {showEmailLabel && (
                <label htmlFor="register-email" className="auth-field__label">
                  Email
                </label>
              )}
              <Input
                id="register-email"
                type="email"
                value={email}
                error={hasEmailError}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearFieldError("email");
                }}
                placeholder="Email"
                disabled={state.status === "submitting"}
                clearable
                onClear={() => {
                  setEmail("");
                  clearFieldError("email");
                }}
              />
            </div>
            <FormError message={errors.email} />
          </FormGroup>
        )}

        {identifierType === "email" && (
          <FormGroup>
            <div className="auth-field">
              {showEmailLabel && (
                <label htmlFor="register-email" className="auth-field__label">
                  Email
                </label>
              )}
              <Input
                id="register-email"
                type="email"
                value={identifier}
                readOnly
                placeholder="Email"
                disabled={state.status === "submitting"}
                className="auth-input-disabled"
              />
            </div>
          </FormGroup>
        )}

        <FormGroup>
          <div className={`auth-field ${hasPasswordError ? "is-error" : ""}`}>
            {showPasswordLabel && (
              <label htmlFor="register-password" className="auth-field__label">
                Пароль
              </label>
            )}
            <Input
              id="register-password"
              type="password"
              value={password}
              error={hasPasswordError}
              onChange={(e) => {
                setPassword(e.target.value);
                clearFieldError("password");
              }}
              placeholder="Пароль (не менее 8 символов)"
              disabled={state.status === "submitting"}
              passwordToggle={true}
            />
          </div>
          <FormError message={errors.password} />
        </FormGroup>

        <FormGroup className="tw-flex tw-flex-col tw-gap-4">
          <div className="auth-options">
            {/* <label className="auth-option">
							<input type="checkbox" className="auth-option__input" checked={isLegalEntity} onChange={(e) => setIsLegalEntity(e.target.checked)} disabled={state.status === "submitting"} />
							<span className="auth-option__box" />
							<span className="auth-option__content">
								<span className="auth-option__label">Я представитель юрлица или ИП</span>
								<span className="auth-option__description">Добавление компаний, быстрые заказы и ЭДО</span>
							</span>
						</label> */}

            <label className="auth-option">
              <input
                type="checkbox"
                className="auth-option__input"
                checked={subscribeToNewsletter}
                onChange={(e) => setSubscribeToNewsletter(e.target.checked)}
                disabled={state.status === "submitting"}
              />
              <span className="auth-option__box" />
              <span className="auth-option__content">
                <span className="auth-option__label">
                  Хочу получать информацию об акциях и специальных предложениях
                </span>
                <span className="auth-option__description">
                  Пишем только по делу
                </span>
              </span>
            </label>
          </div>
        </FormGroup>

        <FormError message={errors.general} />
        <FormError message={state.status === "error" ? state.error : null} />

        {state.status === "success" && (
          <div className="tw-p-3 tw-bg-green-50 tw-text-green-700 tw-rounded tw-text-sm tw-border tw-border-green-200">
            Регистрация успешна! Теперь можете войти.
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          loading={state.status === "submitting"}
          disabled={state.status === "submitting"}
          className="tw-w-full"
        >
          {state.status === "submitting"
            ? "Регистрация..."
            : "Зарегистрироваться"}
        </Button>

        <div
          className={`tw-!mt-6 auth-options ${hasPoliticsError ? "is-error" : ""}`}
        >
          <label className="auth-option">
            <input
              type="checkbox"
              className="auth-option__input"
              checked={isPoliticsAccepted}
              onChange={(e) => {
                setIsPoliticsAccepted(e.target.checked);
                if (e.target.checked) {
                  clearFieldError("politics");
                }
              }}
              disabled={state.status === "submitting"}
            />
            <span className="auth-option__box" />
            <span className="auth-option__content">
              <span className="auth-option__label auth-politics-text">
                Соглашаюсь на{" "}
                <a
                  href="https://carolinashop.ru/include/personal_data_consent.php"
                  className="auth-politics-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  обработку персональных данных
                </a>{" "}
                и с{" "}
                <a
                  href="https://carolinashop.ru/include/public_offer.php"
                  className="auth-politics-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  публичной офертой
                </a>
              </span>
            </span>
          </label>
        </div>

        <FormError message={errors.politics} />
      </form>
    </div>
  );
};

export default RegisterView;
